import {Box} from '@mui/material';
import type React from 'react';

export type StatusTone = 'success' | 'warning' | 'danger' | 'neutral';

export type StatusChipProps = {
  tone: StatusTone;
  /** The state in words — the colour is never the only cue. */
  label: string;
  /**
   * A decorative glyph drawn in place of the dot (hidden from assistive
   * tech: the label carries the state).
   */
  icon?: React.ReactNode;
  /** For a control's aria-describedby: its text is the label alone. */
  id?: string;
};

// The MUI intents carry the status roles: `light` = tint, `dark` = onTint.
const PALETTE: Record<Exclude<StatusTone, 'neutral'>, string> = {
  success: 'success',
  warning: 'warning',
  danger: 'error',
};

/**
 * A non-interactive status: tint, onTint label, decorative dot or icon.
 *
 * `neutral` is a state that is neither good nor bad ("Inactive", "Not
 * connected", a station that no longer reports, #324): `text.secondary` on
 * `surface.page` (5.54:1 light, 7.38:1 dark),
 * with a `line.hairline` edge because the page fill alone is 1.07:1 against
 * the card. The edge is decorative; the words carry the state.
 */
export const StatusChip = ({tone, label, icon, id}: StatusChipProps) => {
  const colors =
    tone === 'neutral'
      ? {
          bgcolor: 'background.default',
          color: 'text.secondary',
          boxShadow: (t: {palette: {divider: string}}) =>
            `inset 0 0 0 1px ${t.palette.divider}`,
          // Its label is a sentence: wrap inside the card rather than
          // overflow it (German runs long).
          minHeight: 24,
          maxWidth: '100%',
          py: 0.5,
          lineHeight: 1.35,
          whiteSpace: 'normal',
        }
      : {
          bgcolor: `${PALETTE[tone]}.light`,
          color: `${PALETTE[tone]}.dark`,
          height: 24,
          lineHeight: 1,
          whiteSpace: 'nowrap',
        };
  return (
    <Box
      component="span"
      id={id}
      data-testid="status-chip"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1,
        borderRadius: '4px',
        fontSize: '0.75rem',
        fontWeight: 500,
        ...colors,
      }}>
      {icon ? (
        <Box
          component="span"
          data-testid="status-chip-icon"
          aria-hidden
          sx={{display: 'inline-flex', fontSize: 14, flexShrink: 0}}>
          {icon}
        </Box>
      ) : (
        <Box
          component="span"
          data-testid="status-chip-dot"
          aria-hidden
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            bgcolor: 'currentColor',
          }}
        />
      )}
      <span>{label}</span>
    </Box>
  );
};
